/** @jsxImportSource @emotion/react */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { addClass } from '@grapecity/wijmo';
import { DataMap, CellType } from '@grapecity/wijmo.grid';
import * as wjGridXlsx from '@grapecity/wijmo.grid.xlsx';
import {
  ControlBtnGroup,
  GlobalBtnGroup,
  SubTitleGroup,
  SubTitleLayout,
} from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import {
  SearchBox,
  SearchBoxRow,
  InputBox,
  SearchRows,
  SearchCols,
} from 'components/layouts/SearchBox';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';
import { ComboBox } from 'components/selects/ComboBox';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import CustomGrid from 'components/grids/CustomGrid';
import UserModal from 'components/modals/common/UserModal';
import DepartmentType1Modal from 'components/modals/common/DepartmentType1Modal';
import { GridStatusCellTemplate } from 'components/grids/GridStatusCellRenderer';
import { getCommonCodeNames, getCommonCodeNamesCondition } from 'apis/admin/CommonCode';
import { Code } from 'models/common/CommonCode';
import { CrudCode } from 'models/common/Edit';
import { CommonYN } from 'models/common/Common';
import {
  IdleAssetReviewManager,
  IdleAssetReviewManagerCondition,
} from 'models/asst/IdleAssetReview';
import { Employee } from 'models/admin/Employee';
import { useMessageBar } from 'hooks/useMessageBar';
import { useCommonModal } from 'hooks/useCommonModal';
import { findIdleAssetReviewManager, saveIdleAssetReviewManager } from 'apis/asst/IdleAssetReview';
import _ from 'lodash';
import { getExcelFileName } from '../../utils/ExcelUtil';
import { toggleClass } from '@grapecity/wijmo';
import * as input from '@grapecity/wijmo.input';
import * as wjGridFilter from '@grapecity/wijmo.grid.filter';
import * as wijmo from '@grapecity/wijmo';
/**
 * 불용자산 법인 부서별 담당자관리
 * @constructor
 */
const IdlAssetReviewManagerPage = () => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const { openCommonModal } = useCommonModal();
  const gridRef = useRef<any>();
  const [condition, setCondition] = useState<IdleAssetReviewManagerCondition>({});
  const [rowData, setRowData] = useState<IdleAssetReviewManager[]>([]);
  const [code, setCode] = useState<any>();
  const [defaultCopCd, setDefaultCopCd] = useState<string>();
  const [userModalCondition, setUserModalCondition] = useState<any>({});
  const [userModalInfrCondition, setUserModalInfrCondition] = useState<any>({});
  const [deptModalCondition, setDeptModalCondition] = useState<any>({});
  const [isOpenUserModal, setOpenUserModal] = useState<boolean>(false);
  const [isOpenUserInfrModal, setOpenUserInfrModal] = useState<boolean>(false);

  const [isOpenDeptModal, setOpenDeptModal] = useState<boolean>(false);
  const [searchCopCd, setSearchCopCd] = useState<string>('');

  useEffect(() => {
    const observer = new MutationObserver(function (mutations) {
      for (let i = 0; i < mutations.length; i++) {
        const mutation = mutations[i];

        const addedNodes = mutation.addedNodes;
        for (let j = 0; j < addedNodes.length; j++) {
          const node = addedNodes[j];

          if (node.nodeType === 1) {
            const el = node;
            if (el.classList && el.classList.contains('wj-dropdown-panel')) {
              if (el.classList.contains('wj-columnfiltereditor')) {
                customizeFilterPopup(node);
              }
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  function customizeFilterPopup(popup: HTMLElement) {
    const editor = wijmo.Control.getControl(popup) as wijmoGridFilter.ColumnFilterEditor;
    if (!editor) return;

    const col = editor.filter.column;
    if (col.binding !== 'prdnProcCds') return;
    const lbHost = popup.querySelector('.wj-listbox');
    if (!lbHost) return;

    const listBox = wijmo.Control.getControl(lbHost) as wijmo.input.ListBox;
    if (!listBox) return;

    // formatItem을 매번 다시 걸면 중첩되므로 기존 핸들러 제거
    listBox.formatItem.removeAllHandlers();

    listBox.formatItem.addHandler((s, e) => {
      const valueObj = s.collectionView.items[e.index];

      const label = e.item.querySelector('label');
      if (!label) return;

      // label 내부 텍스트 노드를 찾기
      const textNode = [...label.childNodes].find((n) => n.nodeType === Node.TEXT_NODE) as Text;
      if (!textNode) return;
      // const newText = '';
      const relValues = valueObj.value;
      let tmpVal = [];
      if (relValues && typeof relValues === 'object') {
        for (let j = 0; j < relValues.length; j++) {
          tmpVal.push(relValues[j]['cmnCdNm'] as string);
        }
        textNode.textContent = ' ' + tmpVal.join(',');
      } else {
        const datamap = col.editor.itemsSource;
        if (relValues.includes(',')) {
          const codes = relValues
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);

          tmpVal = codes.map((cd) => datamap.find((p) => p.cmnCd === cd)?.cmnCdNm).filter(Boolean);
          textNode.textContent = ' ' + tmpVal.join(',');
        }
      }
    });
  }

  useEffect(() => {
    getCommonCodes();
    handleSearch();
  }, []);

  const getCommonCodes = async () => {
    const elmCopCd: Code[] = await getCommonCodeNames('ELM_COP_CD'); // 법인코드
    //담당자유형코드(통보자/검토자)
    const mgrTpCd = await getCommonCodeNamesCondition({
      optValCtn1: 'Y',
      cmnGrCd: 'MGR_TP_CD',
    });
    //공정코드(불용자산관리)
    const prdnProcCd = await getCommonCodeNamesCondition({
      optValCtn5: 'Y',
      cmnGrCd: 'PRDN_PROC_CD',
    });

    const useYn = [
      { cmnCd: 'Y', cmnCdNm: 'Y' },
      { cmnCd: 'N', cmnCdNm: 'N' },
    ];

    setCode({
      elmCopCd: elmCopCd,
      mgrTpCd: mgrTpCd,
      useYn: useYn,
      prdnProcCd: prdnProcCd,
    });
  };

  const [checkedItems, setCheckedItems] = useState([]);
  const onCheckedItemsChanged = useEvent((sender) => {
    // sender.checkedItems.map((checkableItem) => checkableItem.cmnCd);
    setCheckedItems(sender.checkedItems);
  });

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'no',
        header: String(t('com.label.NO', 'NO')),
        align: 'center',
        width: 40,
        isReadOnly: true,
        cellTemplate: (grid) => grid.row._idx + 1,
      },
      {
        header: String(t('com.label.상태', '상태')),
        binding: 'crudKey',
        width: 43,
        align: 'center',
        isReadOnly: true,
        cellTemplate: GridStatusCellTemplate,
      },
      {
        binding: 'copCd',
        header: String(t('asst.label.법인', '법인')),
        align: 'center',
        width: 80,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.elmCopCd || [], 'cmnCd', 'cmnCdNm'),
      },

      {
        binding: 'deptNm',
        header: String(t('asst.label.부서', '부서')),
        align: 'left',
        minWidth: 200,
        // width: '*',
        isReadOnly: true,
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            <Button /> 
        `,
      },
      {
        binding: 'prdnProcCd',
        dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),
        visible: false,
      },
      {
        binding: 'prdnProcCds',
        header: String(t('asst.label.공정', '공정')),
        align: 'left',
        minWidth: 300,
        width: '*',
        dataMap: new DataMap(code?.prdnProcCd || [], 'cmnCd', 'cmnCdNm'),

        cellTemplate: (item) => {
          const codes = item.value
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v);

          item.value = codes;

          const labels = codes
            .map((cd) => code?.prdnProcCd.find((p) => p.cmnCd === cd)?.cmnCdNm)
            .filter(Boolean)
            .join(',');
          //  console.log('result', labels);
          return labels;
        },
        editor: new input.MultiSelect(document.createElement('div'), {
          placeholder: '선택',
          showSelectAllCheckbox: true,
          maxHeaderItems: 5,
          // headerFormat: '{count:n0} items', //Cell에 비해 데이터 내용이 많을 경우....원하는 형태 처리.
          itemsSource: code?.prdnProcCd, //배열
          displayMemberPath: 'cmnCdNm', //목록에 표시되는  항목의 속성
          selectedValuePath: 'cmnCd',

          checkedItemsChanged: (sender) => {
            return sender.checkedItems.map((checkableItem) => checkableItem.cmnCd);
          },
        }),
      },
      // {
      //   binding: 'mgrTpCd',
      //   header: String(t('asst.label.구분', '구분')),
      //   align: 'center',
      //   width: 150,
      //   cssClass: 'WijmoSelect',
      //   dataMap: new DataMap(code?.mgrTpCd || [], 'cmnCd', 'cmnCdNm'),
      // },
      {
        binding: 'wrtUserNms',
        header: String(t('asst.label.검토담당자', '검토담당자')),
        align: 'left',
        minWidth: 200,
        // width: '*',
        isReadOnly: true,
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            <Button /> 
        `,
      },
      {
        binding: 'infrUserNms',
        header: String(t('asst.label.통보담당자', '통보담당자')),
        align: 'left',
        minWidth: 200,
        // width: '*',
        isReadOnly: true,
        cssClass: 'WijmoFind',
        cellTemplate: (params) => `
            <span>${params.value || ''}</span> 
            <Button /> 
        `,
      },
      {
        binding: 'useYn',
        header: String(t('asst.label.사용여부', '사용여부')),
        align: 'center',
        width: 100,
        cssClass: 'WijmoSelect',
        dataMap: new DataMap(code?.useYn || [], 'cmnCd', 'cmnCdNm'),
      },
      {
        binding: 'dataInsUserNm',
        header: String(t('asst.label.등록자', '등록자')),
        align: 'center',
        width: 100,
        isReadOnly: true,
        visible: false,
      },
      {
        binding: 'dataInsDtm',
        header: String(t('asst.label.등록일자', '등록일자')),
        align: 'center',
        width: 120,
        isReadOnly: true,
        visible: false,
      },
      {
        binding: 'dataUpdUserNm',
        header: String(t('asst.label.수정자', '수정자')),
        align: 'center',
        width: 100,
        isReadOnly: true,
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('asst.label.수정일자', '수정일자')),
        align: 'center',
        width: 120,
        isReadOnly: true,
      },
      {
        binding: 'rvwDeptId',
        visible: false,
      },
      {
        binding: 'deptCd',
        visible: false,
      },
      {
        binding: 'wrtUserIds',
        visible: false,
      },
    ];
  }, [code]);

  const onInitialized = (grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    /* grid.loadedRows.addHandler(() => {
     /!* grid.rows.forEach((row) => {
        /!*const multiSelect = grid.columns.find((col) => col.binding === 'prdnProcCd')?.editor;
        if (multiSelect && row.dataItem.getChecks) {
          multiSelect.checkedItems = code.filter((o) => row.dataItem.getChecks.includes(o.cmnCd));
        }*!/
      });*!/
    });*/

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;

      if (ht.panel === grid.cells) {
        const item = grid.rows[ht.row].dataItem;
        const binding = grid.columns[ht.col].binding;

        // 부서 클릭
        if ('deptNm' === binding && item.crudKey === CrudCode.CREATE) {
          setSearchCopCd(item.copCd);
          setDeptModalCondition({
            target: {
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenDeptModal(true);
        } else if ('prdnProcCds' === binding) {
          item.showFilterInput = e.target.checked;
        }
        //통보자 선택
        else if ('infrUserNms' === binding) {
          // 부서가 선택되지 않은 경우
          if (!item.deptCd) {
            openMessageBar({
              type: 'error',
              content: t('asst.msg.부서를 선택해 주세요.', '부서를 선택해 주세요.'),
            });
            return;
          }
          setUserModalInfrCondition({
            wrtUserIds: item.infrUserIds,
            deptNm: item.deptNm,
            deptCd: item.deptCd,
            target: {
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenUserInfrModal(true);
        }
        // 담당자 클릭
        else if ('wrtUserNms' === binding) {
          // 부서가 선택되지 않은 경우
          if (!item.deptCd) {
            openMessageBar({
              type: 'error',
              content: t('asst.msg.부서를 선택해 주세요.', '부서를 선택해 주세요.'),
            });
            return;
          }
          setUserModalCondition({
            wrtUserIds: item.wrtUserIds,
            deptNm: item.deptNm,
            deptCd: item.deptCd,
            target: {
              row: ht.row,
              col: ht.col,
            },
          });
          setOpenUserModal(true);
        }
      }
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.ColumnHeader === panel.cellType) {
      const binding = panel.columns[col].binding;
      // 필수항목 (법인, 구분, 부서, 담당자, 사용여부)
      if (['copCd', 'mgrTpCd', 'deptNm', 'useYn', 'prdnProcCds'].includes(binding)) {
        addClass(cell, 'dot');
      }
    } else if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      // 부서, 담당자 editable css 적용
      if ('deptNm' === binding || 'wrtUserNms' === binding || 'infrUserNms' === binding) {
        cell.ariaReadOnly = false;
      }

      const readonly = item.crudKey !== CrudCode.CREATE;
      if (['copCd', 'mgrTpCd'].includes(binding)) {
        cell.ariaReadOnly = readonly;
        toggleClass(cell, 'WijmoSelect', !readonly);
        if (readonly && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
      if ('deptNm' === binding) {
        cell.ariaReadOnly = readonly;
        toggleClass(cell, 'WijmoFind', !readonly);
        if (readonly && cell.firstElementChild instanceof HTMLButtonElement) {
          cell.firstChild.remove();
        }
      }
    }
  });
  const cellEditEnded = useEvent((s, e) => {
    const col = s.columns[e.col];
    const editor = col.editor;
    if ('prdnProcCds' === col.binding) {
      // console.log('cellEditEnded');
      const selected = editor.checkedItems || [];
      const codes = selected.map((it) => it.cmnCd);

      // savedCodesRef.current = codes;
      // 그리드 셀 업데이트
      s.setCellData(e.row, e.col, codes.join(','));
      const item = s.rows[e.row].dataItem;

      item.prdnProcCds = codes.join(',');

      try {
        item.prdnProcCd = codes.map((v) =>
          code?.prdnProcCd.find((p) => {
            if (p?.cmnCd === v.trim()) {
              return p;
            }
          })
        );
      } catch (e) {
        //   console.log('cellValue', code?.prdnProcCd, cellValue);
      }
    }
  });
  const beginningEdit = useEvent((s, e) => {
    const item = s.rows[e.row].dataItem;
    const col = s.columns[e.col];
    const readonly = item.crudKey !== CrudCode.CREATE;
    if (['copCd', 'mgrTpCd', 'deptNm'].includes(col.binding)) {
      e.cancel = readonly;
    }

    if ('prdnProcCds' === col.binding) {
      setTimeout(() => {
        const cellValue = s.getCellData(e.row, e.col, false) as string[];
        let values = [];
        try {
          values = cellValue.split(',').map((v) =>
            code?.prdnProcCd.find((p) => {
              if (p?.cmnCd === v.trim()) {
                return p;
              }
            })
          );
        } catch (e) {
          //   console.log('cellValue', code?.prdnProcCd, cellValue);
        }

        col.editor.checkedItems = values;
      }, 0); // editor 생성 후 실행
    }
  });

  const handleSearch = () => {
    findIdleAssetReviewManager(condition).then((result) => {
      setRowData(result);
    });
  };

  const handleConditionChange = (name, value) => {
    setCondition({ ...condition, [name]: value });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleExportExcel = () => {
    const book = wjGridXlsx.FlexGridXlsxConverter.saveAsync(gridRef.current, {
      includeColumnHeaders: true,
      includeRowHeaders: true,
    });
    book.sheets[0].name = t('asst.label.법인별 담당자', '법인별 담당자');
    book.saveAsync(getExcelFileName(t('asst.label.법인별 담당자', '법인별 담당자')));
  };

  const handleAddRow = () => {
    const newRow = {
      crudKey: CrudCode.CREATE,
      useYn: CommonYN.Y,
      copCd: defaultCopCd || '',
    } as IdleAssetReviewManager;
    const rows = [newRow, ...rowData];
    setRowData(rows);
  };

  const handleDelRow = () => {
    const selectedRows = gridRef.current.rows.filter((r) => r.isSelected);
    if (!selectedRows) return;

    const selectedIds = selectedRows
      .map((row) => {
        return parseInt(row.index!);
      })
      .reverse();

    selectedIds.forEach((item) => {
      if (rowData[item].crudKey === CrudCode.CREATE) {
        delete rowData[item];
      } else {
        rowData[item].crudKey = CrudCode.DELETE;
      }
    });

    const filteredData = rowData.filter((element) => element !== undefined);
    setRowData(filteredData);
  };

  const validate = () => {
    const valid = (rowData || [])
      .filter((o) => o.crudKey !== CrudCode.DELETE)
      .map((o, index) => {
        const f = (rowData || []).filter(
          (m) =>
            ''.concat(m.copCd || '', m.deptCd || '', m.mgrTpCd || '') ==
            ''.concat(o.copCd || '', o.deptCd || '', o.mgrTpCd || '')
        );

        if (f.length > 1) {
          return `${index + 1} : ${t(
            'asst.msg.중복 부서가 존재합니다.',
            `중복 부서가 존재합니다.`
          )}\n`;
        }

        if (_.isEmpty(o.copCd)) {
          return `${index + 1} : ${t('asst.msg.법인을 선택해 주세요.', `법인을 선택해 주세요.`)}\n`;
        }
        // if (_.isEmpty(o.mgrTpCd)) {
        //   return `${index + 1} : ${t('asst.msg.구분을 선택해 주세요.', `구분을 선택해 주세요.`)}\n`;
        // }
        if (_.isEmpty(o.deptCd)) {
          return `${index + 1} : ${t('asst.msg.부서를 선택해 주세요.', `부서를 선택해 주세요.`)}\n`;
        }
        if (_.isEmpty(o.prdnProcCd) || o.prdnProcCd?.length == 0) {
          return `${index + 1} : ${t('asst.msg.공정을 선택해 주세요.', `공정을 선택해 주세요.`)}\n`;
        }

        if (_.isEmpty(o.wrtUserIds) && _.isEmpty(o.infrUserIds)) {
          return `${index + 1} : ${t(
            'asst.msg.담당자를 선택해 주세요.',
            `담당자를 선택해 주세요.`
          )}\n`;
        }
        if (_.isEmpty(o.useYn)) {
          return `${index + 1} : ${t(
            'asst.msg.사용여부를 선택해 주세요.',
            `사용여부를 선택해 주세요.`
          )}\n`;
        }
      })
      .filter((element) => element !== undefined);

    if (valid.length) {
      openMessageBar({ type: 'error', content: valid.toString() });
      return false;
    }
    return true;
  };

  const handleSave = () => {
    if (!validate()) return;

    openCommonModal({
      modalType: 'confirm',
      content: t('com.label.저장하시겠습니까?', '저장하시겠습니까?'),
      yesCallback: () => {
        // 변경된 데이터
        const params = (rowData || []).filter((o) => !!o.crudKey);

        saveIdleAssetReviewManager(params)
          .then((response) => {
            const msg = response?.data || '';
            if (response.successOrNot === 'N') {
              openMessageBar({
                type: 'error',
                content: msg.toString() || t('com.msg.저장 실패했습니다.', '저장 실패했습니다.'),
              });
              return;
            }
            openMessageBar({
              type: 'confirm',
              content: t('com.label.저장되었습니다.', '저장되었습니다.'),
            });
            handleSearch();
          })
          .catch((err) =>
            openMessageBar({
              type: 'error',
              content: t('com.label.저장 중 오류가 발생했습니다.', '저장 중 오류가 발생했습니다.'),
            })
          );
      },
    });
  };

  return (
    <>
      <SearchBox>
        <SearchBoxRow>
          <InputBox>
            <SearchRows className="FourCol">
              <SearchCols>
                <label>
                  <span>{String(t('asst.label.법인', '법인'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('asst.msg.법인', '법인'))}
                  options={code?.elmCopCd || []}
                  defaultValue={condition?.copCd}
                  onChange={(value) => handleConditionChange('copCd', value)}
                />
                {/*<label>*/}
                {/*  <span>{String(t('asst.label.구분', '구분'))}</span>*/}
                {/*</label>*/}
                {/*<ComboBox*/}
                {/*  placeholder={String(t('asst.msg.구분', '구분'))}*/}
                {/*  options={code?.idlWktTpCd || []}*/}
                {/*  defaultValue={condition?.wktTpCd}*/}
                {/*  onChange={(value) => handleConditionChange('wktTpCd', value)}*/}
                {/*/>*/}
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('asst.label.부서', '부서'))}</span>
                </label>
                <CustomInputWithSearch
                  name="deptNm"
                  placeholder={String(t('asst.msg.부서를 입력해 주세요.', '부서를 입력해 주세요.'))}
                  value={condition?.deptNm}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('asst.label.담당자', '담당자'))}</span>
                </label>
                <CustomInputWithSearch
                  name="wrtUserNm"
                  placeholder={String(
                    t('asst.msg.담당자를 입력해 주세요.', '담당자를 입력해 주세요.')
                  )}
                  value={condition?.wrtUserNm}
                  onChange={(e) => {
                    handleConditionChange(e.target.name, e.target.value);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </SearchCols>
              <SearchCols>
                <label>
                  <span>{String(t('asst.label.공정', '공정'))}</span>
                </label>
                <ComboBox
                  placeholder={String(t('asst.msg.공정', '공정'))}
                  options={code?.prdnProcCd || []}
                  defaultValue={condition?.procCd}
                  onChange={(value) => handleConditionChange('procCd', value)}
                />
                {/*<label>*/}
                {/*  <span>{String(t('asst.label.구분', '구분'))}</span>*/}
                {/*</label>*/}
                {/*<ComboBox*/}
                {/*  placeholder={String(t('asst.msg.구분', '구분'))}*/}
                {/*  options={code?.idlWktTpCd || []}*/}
                {/*  defaultValue={condition?.wktTpCd}*/}
                {/*  onChange={(value) => handleConditionChange('wktTpCd', value)}*/}
                {/*/>*/}
              </SearchCols>
              {/*  <SearchCols>
                <label>{t('com.label.사용여부', '사용여부')}</label>
                <ComboBox
                  placeholder={String(t('asst.msg.전체', '전체'))}
                  options={code?.useYn}
                  defaultValue={condition?.useYn}
                  onChange={(value) => handleConditionChange('useYn', value)}
                />
              </SearchCols>*/}
            </SearchRows>
          </InputBox>
          <SearchBoxButtons setReset={setCondition} onSearch={() => handleSearch()} />
        </SearchBoxRow>
      </SearchBox>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('asst.label.법인별 담당자', '법인별 담당자')}</h3>
          <span className="total">
            {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          <label>{String(t('asst.label.법인', '법인'))}</label>
          <ComboBox
            placeholder={String(t('asst.msg.선택', '선택'))}
            options={code?.elmCopCd || []}
            style={{ width: '100px' }}
            defaultValue={defaultCopCd}
            onChange={(value) => setDefaultCopCd(value)}
          />
          <Button
            css={IconButton.button}
            className="addRow"
            onClick={() => handleAddRow()}
            disableRipple
          >
            {t('com.button.행추가', '행추가')}
          </Button>
          <Button
            css={IconButton.button}
            className="delRow"
            onClick={() => handleDelRow()}
            disableRipple
          >
            {t('com.button.행삭제', '행삭제')}
          </Button>
          <Button
            css={IconButton.button}
            className="Exceldown"
            onClick={handleExportExcel}
            disableRipple
          >
            {t('com.button.다운로드', '화면 다운로드')}
          </Button>
          <Button
            css={IconButton.button}
            className="refresh"
            onClick={() => handleSearch()}
            disableRipple
          >
            {t('com.button.새로고침', '새로고침')}
          </Button>
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        height={600}
        allowPinning={false}
        initialized={onInitialized}
        beginningEdit={beginningEdit}
        cellEditEnding={cellEditEnded}
        selectionChanged={onCheckedItemsChanged}
      />

      <GlobalBtnGroup>
        <Button css={IconButton.button} className="save" onClick={() => handleSave()}>
          {t('com.button.저장', '저장')}
        </Button>
      </GlobalBtnGroup>

      {isOpenDeptModal && (
        <DepartmentType1Modal
          open={isOpenDeptModal}
          close={() => setOpenDeptModal(false)}
          initParam={{
            copCd: searchCopCd,
          }}
          save={(result) => {
            const target = deptModalCondition.target;
            if (target && target?.row > -1) {
              const item = gridRef.current?.rows[target.row].dataItem;
              item.deptCd = result.deptCd;
              item.deptNm = result.deptNm;
              // 부서 정보가 변경된 경우 담당자 정보 초기화
              item.wrtUserIds = '';
              item.wrtUserNms = '';
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}

      {isOpenUserModal && (
        <UserModal
          open={isOpenUserModal}
          close={() => setOpenUserModal(false)}
          title={t('asst.label.담당자 조회', '담당자 조회')}
          defaultUserId={userModalCondition?.wrtUserIds}
          condition={{
            deptNm: userModalCondition?.deptNm || '',
            deptCd: userModalCondition?.deptCd || '',
          }}
          singleSelect={false}
          isFixedDept={true}
          onCallback={(result) => {
            const target = userModalCondition.target;
            if (target && target?.row > -1) {
              const users = (result || []) as Employee[];
              const item = gridRef.current?.rows[target.row].dataItem;
              item.wrtUserNms = users.map((o) => o.empNm).join(', ');
              item.wrtUserIds = users.map((o) => o.userId).join(', ');
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}
      {isOpenUserInfrModal && (
        <UserModal
          open={isOpenUserInfrModal}
          close={() => setOpenUserInfrModal(false)}
          title={t('asst.label.통보담당자 조회', '통보담당자 조회')}
          defaultUserId={userModalInfrCondition?.wrtUserIds}
          condition={{
            deptNm: userModalInfrCondition?.deptNm || '',
            deptCd: userModalInfrCondition?.deptCd || '',
          }}
          singleSelect={false}
          isFixedDept={true}
          onCallback={(result) => {
            const target = userModalInfrCondition.target;
            if (target && target?.row > -1) {
              const users = (result || []) as Employee[];
              const item = gridRef.current?.rows[target.row].dataItem;
              item.infrUserNms = users.map((o) => o.empNm).join(', ');
              item.infrUserIds = users.map((o) => o.userId).join(', ');
              if (item.crudKey !== CrudCode.CREATE) {
                item.crudKey = CrudCode.UPDATE;
              }
              gridRef.current?.collectionView.refresh();
            }
          }}
        />
      )}
    </>
  );
};

export default IdlAssetReviewManagerPage;
